import { Dataset } from '@ca/core/models';
import { DatasetFiles, FileModel, ImagesWithAnnotations } from '../../app/core/models';
import * as FileUtils from '../../utils/file/file-utils';

export function sortDatasetsByDate(datasets: Dataset[], sort: '-date' | 'date' = '-date'): Dataset[] {
    return datasets.sort((d1, d2) => {
        const d1CreatedDate = d1.createdAt ? new Date(d1.createdAt) : new Date();
        const d2CreatedDate = d2.createdAt ? new Date(d2.createdAt) : new Date();
        if (sort === '-date') {
            return d2CreatedDate > d1CreatedDate ? 1 : d2CreatedDate === d1CreatedDate ? 0 : -1;
        } else {
            return d1CreatedDate > d2CreatedDate ? 1 : d1CreatedDate === d2CreatedDate ? 0 : -1;
        }
    });
}

export function hasBackgroundAndAtLeastOneOtherClassSelected(classNames?: string[]): boolean {
    const selectedClasses = classNames || [];
    if (selectedClasses.some((c: string) => isBackgroundClass(c))) {
        return selectedClasses.filter((c: string) => !isBackgroundClass(c)).length > 0;
    }
    return false;
}

export function getAllImagesWithAvailableAnnotationsForSelectedClasses(datasetFiles?: DatasetFiles, classes?: string[]): ImagesWithAnnotations | null {
    if (classes == null || classes.length === 0 || datasetFiles == null || datasetFiles.annotations.length === 0) {
        return null;
    }
    const annotationsDict: {
        classesDictionaryByTrainingImage: Map<string, string[]>;
        annotationUrlsDictionaryByTrainingImage: Map<string, string[]>;
    } = createAnnotationsDictsByTrainingImagesAndFilterByClasses(datasetFiles.annotations, classes);
    // keep only training files that have annotations for ALL selected classes
    const trainingImagesUrls: string[] = [];
    // prepare annotation urls for workflow module input
    const annotationUrls: string[] = [];
    // for each file check if it has annotations for all selected classes - if so submit the annotations - otherwise not
    // also prepare annotation urls for workflow module input for relevant training images
    datasetFiles.files.forEach(file => {
        const trainingImageName = FileUtils.getFileNameWithoutExtension(file.name);
        trainingImagesUrls.push(file.token);
        if (hasAnnotationsForAllClasses(annotationsDict.classesDictionaryByTrainingImage.get(trainingImageName), classes)) {
            const annotationUrlsByTrainingImage = annotationsDict.annotationUrlsDictionaryByTrainingImage.get(trainingImageName);
            // add all annotation urls for current file to workflow module input
            if (annotationUrlsByTrainingImage) {
                annotationUrls.push(...annotationUrlsByTrainingImage);
            }
        }
    });
    return { trainingImagesUrls, annotationUrls };
}

export function hasAnnotationsForAllClasses(existingClasses?: string[], selectedClasses?: string[]): boolean {
    if (selectedClasses != null && selectedClasses.length > 0) {
        if (existingClasses != null && existingClasses.length > 0) {
            const matchingClasses = existingClasses.filter(className => selectedClasses.indexOf(className) > -1);
            // we remove duplicates classes
            // we can have duplicate classes when we have images with scenes
            return matchingClasses.filter((item, index) => matchingClasses.indexOf(item) === index).length === selectedClasses.length;
        } else {
            return false;
        }
    }
    return true;
}

export function createAnnotationsDictsByTrainingImagesAndFilterByClasses(
    annotations: FileModel[],
    selectedClasses: string[],
): { classesDictionaryByTrainingImage: Map<string, string[]>; annotationUrlsDictionaryByTrainingImage: Map<string, string[]> } {
    // we need to return 2 annotation dictionaries with training image name as a key and array of associated
    // classes and urls separately because we want to do it in one loop in order to improve performances
    // since there might be a high number of files and we need both classes and urls for later usage
    const classesDictionaryByTrainingImage: Map<string, string[]> = new Map();
    const annotationUrlsDictionaryByTrainingImage: Map<string, string[]> = new Map();
    // filter out annotated images by selected classes in UI and create a training_image/classes and training_image/annotation_urls dictionaries
    annotations.forEach(annotation => {
        if (selectedClasses.filter(value => annotation.classes.includes(value)).length > 0) {
            const trainingImageName = getTrainingImageNameFromAnnotationName(annotation.name);
            if (classesDictionaryByTrainingImage.get(trainingImageName) == null) {
                classesDictionaryByTrainingImage.set(trainingImageName, []);
            }
            if (annotationUrlsDictionaryByTrainingImage.get(trainingImageName) == null) {
                annotationUrlsDictionaryByTrainingImage.set(trainingImageName, []);
            }
            classesDictionaryByTrainingImage.get(trainingImageName)!.push(...annotation.classes);
            annotationUrlsDictionaryByTrainingImage.get(trainingImageName)!.push(annotation.token);
        }
    });

    return { classesDictionaryByTrainingImage, annotationUrlsDictionaryByTrainingImage };
}

export function isBackgroundClass(className: string) {
    return className.toLowerCase() === 'background';
}

export function getTrainingImageNameFromAnnotationName(annotationName: string): string {
    if (FileUtils.getFileExtension(annotationName) === 'objects') {
        return FileUtils.getFileNameWithoutExtension(annotationName);
    }
    const classIndex: number = annotationName.lastIndexOf('_');
    const annotationNameWithoutClass = annotationName.substring(0, classIndex);
    return annotationNameWithoutClass.replace(/((_S|_s)([0-9]{5}))$/, '');
}
